import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import logo from '../assets/a5.webp';

const navItems = [
  { name: 'Home',          path: '/' },
  { name: 'Start Project', path: '/start-project' },
  { name: 'Portfolio',     path: '/portfolio' },
  { name: 'Services',      path: '/services' },
  { name: 'About',         path: '/blog' },
];

// Pages that have a dark hero banner at the top (transparent header at top)
const HERO_PAGES = ['/', '/start-project', '/portfolio'];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isHeroPage = HERO_PAGES.includes(location.pathname);

  // Detect scroll position
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => { setIsMenuOpen(false); }, [location]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const handleNavClick = (path) => {
    setIsMenuOpen(false);
    if (location.pathname === path) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Determine header background class:
  // - Hero pages (Home, Start Project, Portfolio): transparent when at top, frosted emerald when scrolled
  // - Light pages (Services, About): always frosted emerald glass so text & logo are 100% visible
  const headerClass = isHeroPage
    ? (isScrolled ? 'sakae-header scrolled' : 'sakae-header at-top')
    : 'sakae-header scrolled';

  return (
    <>
      <header className={headerClass}>
        <div className="sakae-header-inner">
          {/* Logo */}
          <Link to="/" className="sakae-logo-link" onClick={() => handleNavClick('/')}>
            <img src={logo} alt="SAKAE Studio" className="sakae-logo-img" />
          </Link>

          {/* Desktop nav */}
          <nav className="sakae-nav" aria-label="Main navigation">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`sakae-nav-link ${location.pathname === item.path ? 'active' : ''}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Hamburger */}
          <button
            className={`sakae-hamburger ${isMenuOpen ? 'open' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`sakae-nav-backdrop ${isMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />
      <nav className={`sakae-mobile-nav ${isMenuOpen ? 'open' : ''}`} aria-label="Mobile navigation">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`sakae-mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
            onClick={() => handleNavClick(item.path)}
          >
            {item.name}
          </Link>
        ))}
      </nav>
    </>
  );
};

export default Header;
